import { ContainerType } from 'src/components/common/container-details/ContainerDetailsModel';
import { GetContainerDetailsOutput } from 'src/models/GetContainerDetails';
import { AUTHENTICATION_FAILED_MESSAGE, PERMISSIONS_DENIED_MESSAGE } from 'src/localization/string-ids/app-strings';
import { DoorScanResult, DoorScanResultType, FailureResultType, ScanResult, ScanResultType, SidelineCheckResult,
    SidelineCheckResultType, Trailer, TrailerIdType, UnloadScanOutcome } from 'src/models/unload';

// What the BFF client rejects with: a session string, an Error, or { errorMessage, errorStatus }
type BffRejection = { errorMessage?: unknown, errorStatus?: number, message?: unknown } | null;

// GetContainer(mode=UNLOAD) names its outcome; any outcome but these three, or none, is an answer the app cannot read,
// a system error
const RESULT_TYPE_BY_UNLOAD_SCAN_OUTCOME = new Map<string | undefined, DoorScanResultType>([
    [UnloadScanOutcome.OK, ScanResultType.ACCEPTED],
    [UnloadScanOutcome.NO_TRAILER_AT_DOOR, ScanResultType.NO_TRAILER],
    [UnloadScanOutcome.UNLOAD_NOT_ENABLED, ScanResultType.NOT_ENABLED]
]);

// What the screens call a trailer the service answers. A yard visit ID is no name an associate can find on a trailer,
// so a trailer keyed by one is shown by its equipment number when the answer has it, and any other by its key
const getTrailerName = (properties: Record<string, string>, trailerId: string): string =>
    (properties.trailerIdType === TrailerIdType.VISIT && properties.equipmentNumber) || trailerId;

// An accepted door answers its trailer by the key the service keeps it under
export const getDoorScanResult = (value: string, output: GetContainerDetailsOutput | undefined): DoorScanResult => {
    const container = output?.container;
    const properties = container?.containerProperties ?? {};
    const outcome = RESULT_TYPE_BY_UNLOAD_SCAN_OUTCOME.get(properties.unloadScanOutcome) ?? ScanResultType.SYSTEM_ERROR;
    if (outcome !== ScanResultType.ACCEPTED) {
        return { outcome, value };
    }
    const trailerId = properties.trailerId;
    if (container?.containerType !== ContainerType.TRAILER || !trailerId) {
        return { outcome: ScanResultType.SYSTEM_ERROR, value };
    }
    const name = getTrailerName(properties, trailerId);
    return { outcome: ScanResultType.ACCEPTED, value, trailer: { doorId: properties.dockDoorId ?? value, trailerId, name,
        count: container.numOfChildContainers } };
};

// A pallet answers itself, linked to a trailer, whose trailer id is its VRID, or received as an orphan on none; both
// open its loop
export const getPalletScanResult = (value: string, output: GetContainerDetailsOutput | undefined): DoorScanResult => {
    const container = output?.container;
    const properties = container?.containerProperties ?? {};
    const outcome = properties.unloadScanOutcome;
    if (outcome === UnloadScanOutcome.UNLOAD_NOT_ENABLED) {
        return { outcome: ScanResultType.NOT_ENABLED, value };
    }
    const trailerId = outcome === UnloadScanOutcome.PALLET_LINKED ? properties.trailerId : undefined;
    if (!(trailerId || outcome === UnloadScanOutcome.PALLET_ORPHAN)
        || container?.containerType !== ContainerType.RETURN_PALLET || !container.containerId) {
        return { outcome: ScanResultType.SYSTEM_ERROR, value };
    }
    return { outcome: ScanResultType.ACCEPTED, value, trailer: { trailerId, count: container.numOfChildContainers,
        palletId: container.containerId } };
};

// GetContainer(mode=UNLOAD) on a sideline ID answers whether it is on a current trailer or pallet, which the next scan
// resolves it under; any outcome but these four, or none, is an answer the app cannot read
const RESULT_TYPE_BY_SIDELINE_OUTCOME = new Map<string | undefined, SidelineCheckResultType>([
    [UnloadScanOutcome.SIDELINE_LINKED, ScanResultType.ACCEPTED],
    [UnloadScanOutcome.SIDELINE_RESOLVED, ScanResultType.ALREADY_RESOLVED],
    [UnloadScanOutcome.SIDELINE_NOT_LINKED, ScanResultType.NOT_LINKED],
    [UnloadScanOutcome.UNLOAD_NOT_ENABLED, ScanResultType.NOT_ENABLED]
]);

// A linked sideline ID names the pallet or trailer it is on, a pallet even when that pallet has a trailer, and one
// already resolved names what it was resolved to
export const getSidelineCheckResult = (value: string,
    output: GetContainerDetailsOutput | undefined): SidelineCheckResult => {
    const properties = output?.container?.containerProperties ?? {};
    const outcome = RESULT_TYPE_BY_SIDELINE_OUTCOME.get(properties.unloadScanOutcome) ?? ScanResultType.SYSTEM_ERROR;
    if (outcome === ScanResultType.ACCEPTED) {
        const { palletId, trailerId } = properties;
        return { outcome, value, parentName: palletId || (trailerId ? getTrailerName(properties, trailerId) : undefined) };
    }
    if (outcome !== ScanResultType.ALREADY_RESOLVED) {
        return { outcome, value };
    }
    const resolvedContainerId = properties.resolvedContainerId;
    return resolvedContainerId ? { outcome, value, resolvedContainerId } : { outcome: ScanResultType.SYSTEM_ERROR, value };
};

// GetContainer(mode=SEARCH) answers the trailer or pallet asked for, with its children counted; an answer of any
// other type has no count of it
export const getChildContainerCount = (output: GetContainerDetailsOutput | undefined,
    type: ContainerType): number | undefined =>
    (output?.container?.containerType === type ? output.container.numOfChildContainers : undefined);

// CreateContainer answers an empty object, so an answer that is no object at all, such as an HTML page, is a system
// error, no evidence the scan landed
export const getLabelScanResult = <Landed extends ScanResultType>(value: string, output: unknown, landed: Landed):
    ScanResult<Landed | ScanResultType.SYSTEM_ERROR> =>
    ({ outcome: typeof output === 'object' && output !== null ? landed : ScanResultType.SYSTEM_ERROR, value });

const getFailureResultType = (rejection: BffRejection | string): FailureResultType => {
    if (typeof rejection === 'string') {
        return [AUTHENTICATION_FAILED_MESSAGE, PERMISSIONS_DENIED_MESSAGE].includes(rejection)
            ? ScanResultType.REFUSED : ScanResultType.SYSTEM_ERROR;
    }
    const status = rejection?.errorStatus;
    // HTTP's own meanings: 408 request timeout, 429 too many requests, 5xx a server fault, and no status a call that
    // got no answer. Any other status, 401 unauthenticated and 403 forbidden included, is a refusal
    if (!status || status === 408 || status === 429 || status >= 500) return ScanResultType.SYSTEM_ERROR;
    return ScanResultType.REFUSED;
};

// Every failure reads the same, whatever was scanned
export const getFailureResult = (value: string, rejection: unknown): ScanResult<FailureResultType> => {
    const failure = rejection as BffRejection | string;
    // A thrown string, an expired session or a missing permission, has no message, so it shows UNKNOWN_ERROR, like in
    // other RIV modes
    const text = typeof failure === 'string' ? undefined : failure?.errorMessage ?? failure?.message;
    return { outcome: getFailureResultType(failure), value,
        message: typeof text === 'string' && text ? text : undefined };
};

// The container a loop scans into: the open pallet, or else the trailer, by its VRID
export const getBoundContainerId = (trailer: Trailer): string =>
    (trailer.palletId === undefined ? trailer.trailerId : trailer.palletId);

// What the screens call what a loop scans into: a pallet by its label, a trailer by its name
export const getBoundContainerName = (trailer: Trailer): string =>
    (trailer.palletId === undefined ? trailer.name : trailer.palletId);
